import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Loader2, TrendingUp, Calendar } from 'lucide-react';
import YearSelector from './YearSelector';
import ExportButton from './ExportButton';
import { useYear } from '../contexts/YearContext';
import { HelpSection, HelpP, HelpList, HelpRule, HelpCallout, HelpHeading } from './HelpSection';

function Reports() {
  const [summary, setSummary] = useState(null);
  const [weeklyEntries, setWeeklyEntries] = useState([]);
  const [monthlyChecks, setMonthlyChecks] = useState([]);
  const [monthlyBreakdown, setMonthlyBreakdown] = useState([]);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  // Tax Summary scope toggle — year-scoped by default, all-time available on demand.
  const [taxScope, setTaxScope] = useState('year');
  const [taxSummary, setTaxSummary] = useState(null);
  const [taxSummaryLoading, setTaxSummaryLoading] = useState(false);
  const { selectedYear } = useYear();

  // Pre-compute totals once per render (not per cell)
  const breakdownTotals = useMemo(() => {
    let gross = 0, afterBusiness = 0, afterAll = 0, expenses = 0;
    for (const m of monthlyBreakdown) {
      gross += m.gross_income || 0;
      afterBusiness += m.net_after_business || 0;
      afterAll += m.net_after_all || 0;
      expenses += (m.business_expenses || 0) + (m.personal_expenses || 0);
    }
    return { gross, afterBusiness, afterAll, expenses };
  }, [monthlyBreakdown]);

  useEffect(() => {
    if (selectedYear) {
      loadData();
    }
  }, [selectedYear]);

  // Fetch Tax Summary whenever scope or year changes.
  useEffect(() => {
    if (!selectedYear) return;
    let cancelled = false;
    (async () => {
      try {
        setTaxSummaryLoading(true);
        const res = await axios.get(`${API}/reports/tax-summary`, {
          params: taxScope === 'year' ? { scope: 'year', year: selectedYear } : { scope: 'all_time' },
        });
        if (!cancelled) setTaxSummary(res.data);
      } catch (err) {
        console.error('Error loading tax summary:', err);
      } finally {
        if (!cancelled) setTaxSummaryLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [taxScope, selectedYear]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [summaryRes, weeklyRes, monthlyRes, breakdownRes, planRes] = await Promise.all([
        axios.get(`${API}/dashboard/summary`),
        axios.get(`${API}/income/weekly?year=${selectedYear}`),
        axios.get(`${API}/income/monthly?year=${selectedYear}`),
        axios.get(`${API}/reports/monthly-breakdown?year=${selectedYear}`),
        axios.get(`${API}/financial-plan`)
      ]);
      setSummary(summaryRes.data);
      setWeeklyEntries(weeklyRes.data);
      setMonthlyChecks(monthlyRes.data);
      setMonthlyBreakdown(breakdownRes.data);
      setPlan(planRes.data);
    } catch (err) {
      console.error('Error loading report data:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="reports-loading">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reports">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Financial Reports</h1>
            <p className="text-gray-600 dark:text-gray-300 dark:text-gray-300">Monthly summaries and year-to-date analysis for {selectedYear}</p>
          </div>
          <div className="flex items-center space-x-4">
            <YearSelector />
            <ExportButton
              endpoint="/exports/ytd-summary.csv"
              params={{ year: selectedYear }}
              filename={`papertrail-ytd-summary-${selectedYear}.csv`}
              label="Export to CSV"
              testId="export-csv-btn"
              disabled={weeklyEntries.length === 0}
            />
          </div>
        </div>
      </div>

      {/* YTD Summary */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1 flex items-center space-x-2">
          <TrendingUp className="w-6 h-6" />
          <span>Year-to-Date Allocation (Based on Income)</span>
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          This shows how your income has been distributed based on your allocation plan, not your current account balances.
          <span className="block text-xs text-gray-500 dark:text-gray-500 mt-1">
            <strong>Income = Personal + Business + Taxes + Savings + Buffer (+ Custom)</strong> — every dollar has exactly one home. <strong>Buffer</strong> holds unallocated capital retained outside your core plan buckets.
          </span>
          <span className="block text-xs text-gray-500 dark:text-gray-500 mt-1">
            <strong>Net Profit = Gross Income − (Business Expenses + Personal Expenses)</strong> — pulled directly from your Expense Tracker.
          </span>
        </p>
        <p className="text-xs uppercase tracking-wide font-semibold text-gray-500 dark:text-gray-400 mb-3">
          Income distribution totals
        </p>
        
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <SummaryCard label="Total Income" value={summary?.total_income_ytd || 0} color="blue" testId="ytd-income" />
          <SummaryCard label={`Taxes Account (${plan?.allocation?.taxes || 20}%)`} value={summary?.total_taxes_ytd || 0} color="red" testId="ytd-taxes" />
          <SummaryCard label={`Business Account (${plan?.allocation?.business || 20}%)`} value={summary?.total_business_ytd || 0} color="purple" testId="ytd-business" />
          <SummaryCard label={`Personal Account (${plan?.allocation?.personal || 55}%)`} value={summary?.total_personal_ytd || 0} color="green" testId="ytd-personal" />
          <SummaryCard label={`Savings Account (${plan?.allocation?.savings || 5}%)`} value={summary?.total_savings_ytd || 0} color="yellow" testId="ytd-savings" />
          {/* Buffer — first-class bucket (Feb 2026). Always rendered; if zero
              it simply reads $0.00, which is fine because it IS a real bucket. */}
          <SummaryCard
            label="Buffer Account"
            value={summary?.total_buffer_ytd || 0}
            color="teal"
            testId="ytd-buffer"
            subtitle="Unallocated capital outside plan buckets"
          />
          {/* Feb 2026 — Custom allocation buckets are now first-class in the
              YTD Allocation view so Σ(primary + custom) reconciles to Total
              Income. Before this render, the primary cards silently masked
              a `gross × Σ custom_pct` over-allocation from the v2 writer. */}
          {Object.entries(summary?.total_custom_allocations_ytd || {}).map(([name, value]) => (
            <SummaryCard
              key={`ytd-custom-${name}`}
              label={`${name} (Custom)`}
              value={value || 0}
              color="orange"
              testId={`ytd-custom-${name.toLowerCase().replace(/\s+/g, '-')}`}
              subtitle="Custom allocation bucket"
            />
          ))}
          <SummaryCard
            label="Net Profit"
            value={summary?.total_net_profit_ytd || 0}
            color="teal"
            testId="ytd-net-profit"
            subtitle={`Gross − $${(summary?.total_expenses_ytd || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })} expenses`}
          />
        </div>
      </div>

      {/* Monthly Gross vs Net Income Chart */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
          <TrendingUp className="w-6 h-6" />
          <span>Monthly Income Comparison</span>
        </h2>
        <MonthlyIncomeChart monthlyBreakdown={monthlyBreakdown} />
      </div>

      {/* Performance Metrics */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Performance Metrics</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <MetricCard
            label="Average Weekly Income"
            value={`$${(summary?.avg_weekly_income || 0).toLocaleString()}`}
            testId="metric-avg-income"
          />
          <MetricCard
            label="Business Requirements Met"
            value={`${summary?.business_req_met_count || 0} / ${summary?.weeks_logged || 0} weeks`}
            testId="metric-business-req"
          />
          <MetricCard
            label="Personal Requirements Met"
            value={`${summary?.personal_req_met_count || 0} / ${summary?.weeks_logged || 0} weeks`}
            testId="metric-personal-req"
          />
        </div>
      </div>

      {/* Monthly Breakdown */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
          <Calendar className="w-6 h-6" />
          <span>Monthly Breakdown</span>
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="monthly-breakdown-table">
            <thead className="bg-blue-600 text-white">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Month</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Month Start</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Month End</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase bg-blue-600">Gross Income</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase bg-orange-600">After Business Expenses</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase bg-green-600">Net Profit</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase bg-red-600">Total Expenses</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700 dark:divide-gray-700">
              {monthlyBreakdown.map((month, index) => {
                const grossIncome = month.gross_income || 0;
                const netAfterBusiness = month.net_after_business || 0;
                const netAfterAll = month.net_after_all || 0;
                const totalLoss = (month.business_expenses || 0) + (month.personal_expenses || 0);
                
                return (
                  <tr key={month.month} className={index % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-700'} data-testid={`month-row-${month.month.toLowerCase()}`}>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">{month.month}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{month.month_start}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{month.month_end}</td>
                    <td className="px-4 py-3 text-sm font-bold text-blue-600 dark:text-blue-400 text-right">
                      ${grossIncome.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="px-4 py-3 text-sm font-bold text-orange-600 dark:text-orange-400 text-right">
                      ${netAfterBusiness.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className={`px-4 py-3 text-sm font-bold text-right ${
                      netAfterAll >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                    }`}>
                      ${netAfterAll.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="px-4 py-3 text-sm font-bold text-red-600 dark:text-red-400 text-right">
                      ${totalLoss.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-gray-100 dark:bg-gray-700">
              <tr>
                <td colSpan="3" className="px-4 py-3 text-sm font-bold text-gray-900 dark:text-white">TOTALS</td>
                <td className="px-4 py-3 text-sm font-bold text-blue-600 dark:text-blue-400 text-right">
                  ${breakdownTotals.gross.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </td>
                <td className="px-4 py-3 text-sm font-bold text-orange-600 dark:text-orange-400 text-right">
                  ${breakdownTotals.afterBusiness.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </td>
                <td className="px-4 py-3 text-sm font-bold text-green-600 dark:text-green-400 text-right">
                  ${breakdownTotals.afterAll.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </td>
                <td className="px-4 py-3 text-sm font-bold text-red-600 dark:text-red-400 text-right">
                  ${breakdownTotals.expenses.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Tax Summary */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6" data-testid="tax-summary">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Tax Summary</h2>
          <div
            className="inline-flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1"
            role="tablist"
            aria-label="Tax summary scope"
            data-testid="tax-scope-toggle"
          >
            <button
              onClick={() => setTaxScope('year')}
              role="tab"
              aria-selected={taxScope === 'year'}
              className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                taxScope === 'year'
                  ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
              }`}
              data-testid="tax-scope-year"
            >
              This Year ({selectedYear})
            </button>
            <button
              onClick={() => setTaxScope('all_time')}
              role="tab"
              aria-selected={taxScope === 'all_time'}
              className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                taxScope === 'all_time'
                  ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
              }`}
              data-testid="tax-scope-all-time"
            >
              All Time
            </button>
          </div>
        </div>

        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-4">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-1">
                Tax Reserve ({taxScope === 'year' ? selectedYear : 'All Time'})
              </p>
              <p
                className="text-4xl font-bold text-red-600 dark:text-red-400"
                data-testid="tax-reserve-amount"
              >
                ${(taxSummary?.tax_reserve || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-1">
                Current Balance ({taxScope === 'year' ? selectedYear : 'All Time'})
              </p>
              <p
                className="text-2xl font-bold text-gray-900 dark:text-white"
                data-testid="tax-current-balance"
              >
                ${(taxSummary?.current_balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {taxScope === 'year' ? (
              <>
                <strong>Tax Reserve</strong> = Gross Income × Tax % for {selectedYear}. <strong>Current Balance</strong> = Tax Reserve − any tax payments logged this year (canonical engine, year-scoped).
              </>
            ) : (
              <>
                <strong>Tax Reserve</strong> = lifetime allocated to taxes across all years. <strong>Current Balance</strong> = lifetime reserve − all tax payments (canonical engine, all-time).
              </>
            )}
            {taxSummaryLoading && <span className="ml-2 italic opacity-60">updating…</span>}
          </p>
        </div>
      </div>

      {/* How this works */}
      <HelpSection title="📊 How Reports Work" testId="help-reports">
        <HelpP>This page shows how your income has been distributed over time.</HelpP>
        <HelpRule />
        <HelpCallout tone="amber">
          ⚠️ <strong>Important:</strong> These numbers reflect <em>allocation based on your income</em> — not your current account balances.
        </HelpCallout>
        <HelpHeading>📈 What you&apos;re seeing:</HelpHeading>
        <HelpList
          items={[
            <><strong>Total Income</strong> → All income logged (Weekly + Monthly)</>,
            <><strong>Allocation totals</strong> → Sum of per-entry persisted splits for each bucket (Personal, Business, Taxes, Savings, <strong>Buffer</strong>, + Custom). Closed system: they reconcile exactly to Total Income.</>,
            <><strong>Buffer</strong> → Unallocated capital retained outside the core plan buckets. Not savings. Not profit. It&apos;s a real bucket you can move in/out of via Rebalance.</>,
            <><strong>Net Profit</strong> → Gross Income − (Business Expenses + Personal Expenses) from your Expense Tracker</>,
            <><strong>Monthly trends</strong> → Gross vs. After Business Expenses vs. Net Profit, month-over-month</>,
            <><strong>Tax Summary</strong> → Toggle between this year and all-time to see year-scoped reserves or lifetime totals</>,
          ]}
        />
        <HelpHeading>🎯 Use this page to:</HelpHeading>
        <HelpList
          items={[
            'Track growth',
            'Understand spending patterns',
            'See how your system performs over time',
          ]}
        />
      </HelpSection>
    </div>
  );
}

function SummaryCard({ label, value, color, testId, subtitle }) {
  const colorClasses = {
    blue: 'bg-blue-50 dark:bg-blue-900/30 text-blue-900 dark:text-blue-100',
    red: 'bg-red-50 dark:bg-red-900/30 text-red-900 dark:text-red-100',
    purple: 'bg-purple-50 dark:bg-purple-900/30 text-purple-900 dark:text-purple-100',
    green: 'bg-green-50 dark:bg-green-900/30 text-green-900 dark:text-green-100',
    yellow: 'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-900 dark:text-yellow-100',
    teal: 'bg-teal-50 dark:bg-teal-900/30 text-teal-900 dark:text-teal-100',
    orange: 'bg-orange-50 dark:bg-orange-900/30 text-orange-900 dark:text-orange-100',
  };

  return (
    <div className={`p-4 rounded-lg ${colorClasses[color]}`} data-testid={testId}>
      <p className="text-xs font-medium mb-1 opacity-75">{label}</p>
      <p className="text-xl font-bold">
        ${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </p>
      {subtitle && <p className="text-[11px] mt-1 opacity-70">{subtitle}</p>}
    </div>
  );
}

function MetricCard({ label, value, testId }) {
  return (
    <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg" data-testid={testId}>
      <p className="text-sm text-gray-600 dark:text-gray-300 mb-1">{label}</p>
      <p className="text-2xl font-bold text-gray-900 dark:text-white dark:text-white">{value}</p>
    </div>
  );
}

// Monthly Income Chart Component - Triple Bar Graph
function MonthlyIncomeChart({ monthlyBreakdown }) {
  // Enhanced null/undefined checks to prevent toLocaleString errors
  if (!monthlyBreakdown || !Array.isArray(monthlyBreakdown) || monthlyBreakdown.length === 0) {
    return (
      <div className="text-center text-gray-500 dark:text-gray-400 py-8">
        No monthly data available yet. Start logging income to see your monthly comparison!
      </div>
    );
  }

  // Find the maximum value and round up to nearest $5,000
  // Additional safety check to ensure all values are numbers
  const dataMaxValue = Math.max(
    ...monthlyBreakdown.map(month => {
      const gross = Number(month?.gross_income) || 0;
      const business = Number(month?.net_after_business ?? month?.net_after_expenses) || 0;
      const all = Number(month?.net_after_all ?? month?.net_after_expenses) || 0;
      return Math.max(gross, Math.abs(business), Math.abs(all));
    })
  );
  const maxValue = Math.ceil(dataMaxValue / 5000) * 5000; // Round up to nearest $5,000

  // Generate Y-axis labels - show at most 5-6 labels in $5,000 or $10,000 or $15,000 increments
  const yAxisLabels = [];
  const numIntervals = maxValue / 5000;
  
  if (isNaN(maxValue) || maxValue === 0) {
    // Default labels if no data
    yAxisLabels.push(0, 5000, 10000, 15000, 20000);
  } else if (numIntervals <= 5) {
    // Show every $5,000
    for (let i = 0; i <= numIntervals; i++) {
      yAxisLabels.push(i * 5000);
    }
  } else if (numIntervals <= 10) {
    // Show every $10,000
    for (let i = 0; i <= numIntervals / 2; i++) {
      yAxisLabels.push(i * 10000);
    }
  } else {
    // Show every $15,000 or $20,000
    const step = Math.ceil(maxValue / 5 / 5000) * 5000;
    for (let i = 0; i * step <= maxValue; i++) {
      yAxisLabels.push(i * step);
    }
  }
  
  yAxisLabels.reverse(); // Show highest at top

  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-600 dark:text-gray-300">
        Compare your gross income against net profit after expenses for each month
      </p>

      {/* Chart Container */}
      <div className="relative bg-gray-50 dark:bg-gray-700 p-6 rounded-lg border">
        {/* Y-Axis Labels */}
        <div className="absolute left-0 top-0 bottom-0 w-24 flex flex-col justify-between pr-2" style={{ paddingTop: '60px', paddingBottom: '46px' }}>
          {yAxisLabels.map((value, idx) => (
            <span key={`y-axis-${value}-${idx}`} className="text-xs text-gray-500 dark:text-gray-400 text-right">
              ${(value || 0).toLocaleString()}
            </span>
          ))}
        </div>

        {/* Chart Area */}
        <div className="ml-24 relative overflow-x-auto" style={{ minHeight: '460px', paddingTop: '60px' }}>
          {/* Grid Lines - contained within chart area, not extending to labels */}
          <div className="absolute" style={{ top: 0, left: 0, right: 0, bottom: '40px', height: '360px' }}>
            {yAxisLabels.map((value) => (
              <div
                key={value}
                className="absolute w-full border-t border-gray-300 dark:border-gray-600"
                style={{ bottom: `${(value / maxValue) * 100}%` }}
              />
            ))}
          </div>

          {/* Bars Container */}
          <div className="flex flex-nowrap justify-around items-end relative" style={{ height: '360px', minWidth: '1000px' }}>
            {monthlyBreakdown.map((month, index) => {
              const chartHeight = 360;
              // Ensure all values are numbers to prevent toLocaleString errors
              const grossIncome = Number(month?.gross_income) || 0;
              const netBusiness = Number(month?.net_after_business ?? month?.net_after_expenses) || 0;
              const netAll = Number(month?.net_after_all ?? month?.net_after_expenses) || 0;
              
              const grossHeightPx = (grossIncome / maxValue) * chartHeight;
              const netBusinessHeightPx = (Math.abs(netBusiness) / maxValue) * chartHeight;
              const netAllHeightPx = (Math.abs(netAll) / maxValue) * chartHeight;
              const isBusinessPositive = netBusiness >= 0;
              const isAllPositive = netAll >= 0;

              return (
                <div key={month.month} className="relative flex flex-col items-center h-full flex-shrink-0" style={{ flex: '1 1 0', minWidth: '70px' }}>
                  {/* Bars Group - 3 bars now (thinner) */}
                  <div className="flex items-end space-x-1 w-full justify-center" style={{ height: `${chartHeight}px` }}>
                    {/* 1. Gross Income Bar (Blue) */}
                    <div 
                      className="relative group" 
                      style={{ width: '20%', height: `${grossHeightPx}px`, minHeight: grossIncome > 0 ? '20px' : '0px' }}
                    >
                      <div className="bg-blue-500 rounded-t transition-all hover:bg-blue-600 cursor-pointer w-full h-full"></div>
                      {grossIncome > 0 && (
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-20 pointer-events-none whitespace-nowrap">
                          <div className="bg-gray-800 text-white text-xs rounded py-1 px-2 shadow-lg">
                            Gross: ${grossIncome.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                        </div>
                      )}
                    </div>

                    {/* 2. After Business Expenses Bar (Orange) */}
                    <div 
                      className="relative group" 
                      style={{ width: '20%', height: `${netBusinessHeightPx}px`, minHeight: netBusiness !== 0 ? '20px' : '0px' }}
                    >
                      <div className={`${isBusinessPositive ? 'bg-orange-500 hover:bg-orange-600' : 'bg-red-500 hover:bg-red-600'} rounded-t transition-all cursor-pointer w-full h-full`}></div>
                      {netBusiness !== 0 && (
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-20 pointer-events-none whitespace-nowrap">
                          <div className="bg-gray-800 text-white text-xs rounded py-1 px-2 shadow-lg">
                            After Business: ${netBusiness.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                        </div>
                      )}
                    </div>

                    {/* 3. After All Expenses Bar (Green/Red) */}
                    <div 
                      className="relative group" 
                      style={{ width: '20%', height: `${netAllHeightPx}px`, minHeight: netAll !== 0 ? '20px' : '0px' }}
                    >
                      <div className={`${isAllPositive ? 'bg-green-500 hover:bg-green-600' : 'bg-red-600 hover:bg-red-700'} rounded-t transition-all cursor-pointer w-full h-full`}></div>
                      {netAll !== 0 && (
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-20 pointer-events-none whitespace-nowrap">
                          <div className="bg-gray-800 text-white text-xs rounded py-1 px-2 shadow-lg">
                            Net Profit: ${netAll.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Month Labels Row - Positioned below chart with spacing */}
          <div className="flex flex-nowrap justify-around relative" style={{ minWidth: '1000px', marginTop: '16px' }}>
            {monthlyBreakdown.map((month) => (
              <div key={`label-${month.month}`} className="text-xs text-gray-700 dark:text-gray-300 font-medium text-center flex-shrink-0" style={{ flex: '1 1 0', minWidth: '70px' }}>
                {month.month.substring(0, 3)}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="flex justify-center items-center space-x-4 flex-wrap">
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 bg-blue-500 rounded"></div>
          <span className="text-sm text-gray-700 dark:text-gray-300">Gross Income</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 bg-orange-500 rounded"></div>
          <span className="text-sm text-gray-700 dark:text-gray-300">After Business Expenses</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 bg-green-500 rounded"></div>
          <span className="text-sm text-gray-700 dark:text-gray-300">Net Profit (Gross − All Expenses)</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 bg-red-600 rounded"></div>
          <span className="text-sm text-gray-700 dark:text-gray-300">Net Loss</span>
        </div>
      </div>
    </div>
  );
}

export default Reports;
